import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../logo.png';
import appStoreBadge from '../img/Store=App Store, Type=Dark, Language=English.svg';
import playStoreBadge from '../img/Store=Google Play, Type=Dark, Language=English.svg';
import AppHeader from '../components/AppHeader';

type FeatureSection = {
  title: string;
  description: string;
  images: Array<{ src: string; alt: string }>;
  fullWidthImage?: { src: string; alt: string };
  tone: 'plain' | 'tint' | 'soft';
};

const basePath = (process.env.PUBLIC_URL || '') + '/media/home';

const sections: FeatureSection[] = [
  {
    title: 'One simple place for all your pet’s data',
    description:
      'Keep every important detail together so you can find it instantly.',
    images: [
      { src: 'frame-1.png', alt: 'Pet profile card with photos and quick actions.' },
    ],
    tone: 'plain',
  },
  {
    title: 'All your pet’s details, right in your hand',
    description:
      'Store medications, food, allergies, and vaccinations in one easy-to-share place.',
    images: [
      { src: 'frame-2.png', alt: 'Pet profile showing summary details and images.' },
    ],
    tone: 'tint',
  },
  {
    title: 'Track heart rate, exercise, diet, or add a quick note',
    description:
      'Jump straight into the exact tracking flow you need.',
    images: [
      { src: 'frame-3.png', alt: 'Track menu showing health metric shortcuts.' },
    ],
    tone: 'soft',
  },
  {
    title: 'Easy tools to track heart & respiratory rates',
    description:
      'Guided prompts make capturing vitals fast and accurate.',
    images: [
      { src: 'frame-4.png', alt: 'Guided heart rate tracking instructions.' },
    ],
    tone: 'plain',
  },
  {
    title: 'Visualize heart & respiratory rates over time',
    description:
      'Charts help you spot changes and share progress with your vet.',
    images: [
      { src: 'frame-5.png', alt: 'Health tracker charts for heart and respiratory rate.' },
    ],
    tone: 'tint',
  },
  {
    title: 'Quickly add pictures, medications, & vaccinations',
    description:
      'Use the quick add menu to capture photos and health records in seconds.',
    images: [
      { src: 'frame-6.png', alt: 'Pet detail quick add flyout with options for pictures, medications, food, allergies, vaccinations, and microchip.' },
    ],
    tone: 'soft',
  },
  {
    title: 'Choose your theme and make the app your own',
    description:
      'Pick your preferred color and appearance to personalize the experience.',
    images: [
      { src: 'frame-7.png', alt: 'V2 Theme screen with color and appearance options for personalizing the app.' },
    ],
    tone: 'plain',
  },
  {
    title: 'All your clinic details, right when you need them',
    description:
      'Save favorites and keep maps, contact info, and hours one tap away.',
    images: [
      { src: 'frame-8.png', alt: 'Clinic card with map and contact actions.' },
    ],
    tone: 'tint',
  },
  {
    title: 'Share your pets information with your vet',
    description:
      'Simple and easy way to instantly share your pets details and health with your veterinarian.',
    images: [
      { src: 'frame-9.png', alt: 'Edit medication form with dosage fields.' },
    ],
    fullWidthImage: {
      src: 'frame-10.png',
      alt: 'Share summary action highlighted below the main screen.',
    },
    tone: 'soft',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/' } });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <style>
        {`
          :root {
            --brand-blue: #2d69d8;
            --brand-blue-dark: #1c4faf;
            --brand-ink: #0f1b2d;
            --brand-soft: #f3f6fb;
            --brand-tint: #eaf0ff;
          }
        `}
      </style>

      <AppHeader
        isDarkMode={isDarkMode}
        onToggleTheme={handleThemeToggle}
        currentUser={null}
        onSignIn={handleSignIn}
        onCreateAccount={handleCreateAccount}
        hideHelpButton
        hideCreateAccountButton
      />

      <section
        className="w-full border-b border-slate-200/60"
        style={{
          backgroundImage: `linear-gradient(120deg, rgba(15,27,45,0.55) 0%, rgba(15,27,45,0.15) 60%, rgba(15,27,45,0.0) 100%), url('${basePath}/hero-test.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '624px',
        }}
      >
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-xl rounded-3xl bg-white/60 p-6 shadow-xl backdrop-blur-sm sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-700">
              Healthy pets. Happier tails.
            </p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight text-slate-900 sm:text-5xl">
              Effortless tracking for your pet’s health and essential information.
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Happy Tail Tracker is a free app designed for pet parents to track their pet’s health, milestones, and everyday moments in one simple, organized timeline.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={process.env.REACT_APP_APP_STORE_URL || '/download'}
                className="rounded-xl bg-black shadow-lg shadow-black/20"
              >
                <img src={appStoreBadge} alt="Download on the App Store" className="h-14 w-auto" />
              </Link>
              <Link
                to={process.env.REACT_APP_PLAY_STORE_URL || '/download'}
                className="rounded-xl bg-black shadow-lg shadow-black/20"
              >
                <img src={playStoreBadge} alt="Get it on Google Play" className="h-14 w-auto" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-[var(--brand-soft)]">
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5">
            <h2 className="text-4xl font-semibold text-slate-900 sm:text-5xl">
              Everything you need, organized around your pet.
            </h2>
            <p className="text-lg text-slate-700">You can use it to:</p>
            <ul className="list-disc space-y-2 pl-6 text-base text-slate-700">
              <li>Collect and quickly find photos of your pet without endless scrolling</li>
              <li>Store all important information in one place like microchip details, rabies and other vaccinations, food, feeding schedules, and notes</li>
              <li>Keep a living timeline of health updates, milestones, and daily moments</li>
              <li>Add users so multiple people can share and update information for the same pet, all in one place</li>
              <li>Give subscribed vets access to the information you choose, so they can update medications, add notes, and help manage care directly in the app</li>
              <li>Easily share that information with a vet, pet sitter, or boarding facility</li>
              <li>Locate vets and other pet services wherever you are</li>
            </ul>
            <p className="text-base text-slate-700">
              What makes this app different is that it isn’t vet dependent. Your pet’s information isn’t locked into one system or one provider. If you’re traveling and your pet gets sick or hurt, you can find a vet near you and share your pet’s information directly through the app.
            </p>
            <p className="text-base font-semibold text-slate-900">
              Most importantly, you own your data. Not the vet. Not the app. You.
            </p>
          </div>
        </div>
      </section>

      <section className="flex flex-col pb-0">
        {sections.map((section, index) => {
          const isReversed = index % 2 !== 0;
          const toneClass =
            section.tone === 'tint'
              ? 'bg-[var(--brand-tint)]'
              : section.tone === 'soft'
                ? 'bg-[var(--brand-soft)]'
                : 'bg-white';

          return (
            <div
              key={section.title}
              className={`${toneClass} w-full`}
            >
              <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
                <div className={`grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center ${isReversed ? 'lg:grid-cols-[1.1fr_1fr]' : ''}`}>
                  <div className={isReversed ? 'lg:order-2' : ''}>
                    <h3 className="mt-3 text-2xl font-semibold text-slate-900">
                      {section.title}
                    </h3>
                    <p className="mt-3 text-base text-slate-600">{section.description}</p>
                  </div>

                  <div className={`grid gap-4 ${isReversed ? 'lg:order-1' : ''} ${section.images.length > 2 ? 'sm:grid-cols-2' : ''}`}>
                    {section.images.map((image) => (
                      <div
                        key={image.src}
                        className="overflow-hidden flex justify-center"
                      >
                        <img
                          src={`${basePath}/${image.src}`}
                          alt={image.alt}
                          className="h-full"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {section.fullWidthImage ? (
                  <div className="mt-6">
                    <p className="text-2xl font-semibold text-slate-900">What your veterinarian sees</p>
                    <p className="mt-3 text-base text-slate-600">
                      A clean summary view with the details they need to act quickly.
                    </p>
                    <div className="mt-8 overflow-hidden rounded-2xl">
                      <img
                        src={`${basePath}/${section.fullWidthImage.src}`}
                        alt={section.fullWidthImage.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>

      <footer className="bg-[var(--brand-blue)] text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-0 px-4 py-10 pt-0 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center">
              <img src={logo} alt="Happy Tail Tracker" className="h-96 w-auto" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={process.env.REACT_APP_APP_STORE_URL || '/download'}
                className="rounded-xl bg-black/90 shadow-lg shadow-black/30"
              >
                <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
              </Link>
              <Link
                to={process.env.REACT_APP_PLAY_STORE_URL || '/download'}
                className="rounded-xl bg-black/90 shadow-lg shadow-black/30"
              >
                <img src={playStoreBadge} alt="Get it on Google Play" className="h-12 w-auto" />
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/20 pt-4 text-sm text-white/80">
            <div className="flex flex-wrap items-center gap-3">
              <span>&copy; {new Date().getFullYear()} Happy Tail Tracker</span>
              <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/support" className="hover:text-white">Support</Link>
              <Link to="/delete" className="hover:text-white">Delete</Link>
            </div>
            <div className="text-white/70">Made with love for the pets you love.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
